// js/lib/install.js
// PWA install prompt manager.
//
// Captures the browser's `beforeinstallprompt` event so the app can trigger
// the native Add-to-Home-Screen dialog at any time — instead of relying on
// Chrome's engagement-based timing (which can be days after the first visit).
//
// Usage:
//   import { canInstallPWA, promptInstall, onInstallable } from './lib/install.js';
//
//   onInstallable(() => showMyInstallButton());
//   button.onclick = () => promptInstall();

// ─── State ────────────────────────────────────────────────────────────────────

let _deferred = null; // cached BeforeInstallPromptEvent
let _installed = false; // true once the app is installed this session

// ─── Event capture ────────────────────────────────────────────────────────────

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Store the event so promptInstall() can trigger it manually later
    // (e.g. from an in-app "Install" button).
    // We do NOT call e.preventDefault() so Chrome's native mini-infobar
    // is shown automatically — this is the desired behaviour.
    _deferred = e;
    console.debug("[install] PWA is installable — prompt ready.");
    window.dispatchEvent(new CustomEvent("pwa:installable"));
  });

  window.addEventListener("appinstalled", () => {
    _deferred = null;
    _installed = true;
    console.debug("[install] PWA has been installed.");
    window.dispatchEvent(new CustomEvent("pwa:installed"));
  });
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns true if the native install prompt is currently available.
 * Only works on Android Chrome / Edge / Samsung Internet.
 * iOS Safari does not support this API — users must use Share → Add to Home Screen.
 */
export function canInstallPWA() {
  return _deferred !== null;
}

/**
 * Returns true if the app was just installed in this browser session.
 */
export function isPWAInstalled() {
  return (
    _installed ||
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/**
 * Triggers the native PWA install prompt.
 * Returns true if the user accepted, false if dismissed.
 * Returns false if no prompt is available.
 */
export async function promptInstall() {
  if (!_deferred) return false;
  try {
    _deferred.prompt();
    const { outcome } = await _deferred.userChoice;
    _deferred = null;
    console.debug("[install] Install prompt outcome:", outcome);
    return outcome === "accepted";
  } catch (err) {
    console.warn("[install] promptInstall error:", err);
    return false;
  }
}

/**
 * Registers a callback to run when the PWA becomes installable.
 * If it's already installable (event fired before this call), the callback
 * runs immediately.
 *
 * @param {() => void} cb
 */
export function onInstallable(cb) {
  if (_deferred) {
    cb();
    return;
  }
  window.addEventListener("pwa:installable", cb, { once: true });
}

/**
 * Apply per-shop PWA branding to the current document.
 *
 * - Updates <link rel="apple-touch-icon"> so iOS Safari uses the shop logo
 *   when the user adds the page to their Home Screen.
 * - Updates <meta name="theme-color"> to match the shop's primary colour.
 *
 * Call this once the shop document is available (after Firestore load).
 * Safe to call multiple times — DOM is only mutated when values change.
 *
 * @param {{ name?: string, appName?: string, logoURL?: string, landingTheme?: object }} shop
 */
export function applyShopPWABranding(shop) {
  if (!shop) return;

  // ── Apple touch icon ───────────────────────────────────────────────────────
  // iOS Safari uses this tag (not the manifest) for the home screen icon.
  // The tag must point to an image iOS can render — PNG/JPG/WebP all work;
  // SVG is NOT supported by iOS for apple-touch-icon.
  const logoURL = shop.logoURL;
  if (logoURL) {
    let link = document.querySelector("link[rel='apple-touch-icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "apple-touch-icon";
      document.head.appendChild(link);
    }
    if (link.href !== logoURL) {
      link.href = logoURL;
    }
  }

  // ── Theme colour ───────────────────────────────────────────────────────────
  const themeColor = shop.landingTheme?.colorPrimary;
  if (themeColor) {
    const meta = document.querySelector("meta[name='theme-color']");
    if (meta && meta.content !== themeColor) {
      meta.content = themeColor;
    }
  }

  // ── Document title + iOS home screen name ─────────────────────────────────
  const shopName = shop.appName || shop.name;
  if (shopName) {
    if (document.title !== shopName) {
      document.title = shopName;
    }
    // iOS Safari reads <meta name="apple-mobile-web-app-title"> for the
    // "Add to Home Screen" pre-filled name — update it to the shop name.
    let appTitleMeta = document.querySelector(
      "meta[name='apple-mobile-web-app-title']",
    );
    if (!appTitleMeta) {
      appTitleMeta = document.createElement("meta");
      appTitleMeta.name = "apple-mobile-web-app-title";
      document.head.appendChild(appTitleMeta);
    }
    if (appTitleMeta.content !== shopName) {
      appTitleMeta.content = shopName;
    }

    let applicationNameMeta = document.querySelector(
      "meta[name='application-name']",
    );
    if (!applicationNameMeta) {
      applicationNameMeta = document.createElement("meta");
      applicationNameMeta.name = "application-name";
      document.head.appendChild(applicationNameMeta);
    }
    if (applicationNameMeta.content !== shopName) {
      applicationNameMeta.content = shopName;
    }
  }
}
